import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/da25rp5v7gurs51ef7j0';

const stats = [
  { value: '180+', label: '累计甄选作品' },
  { value: '8', label: '覆盖主题分类' },
  { value: '1200+', label: '漫友投稿安利' },
  { value: '每周', label: '更新安利清单' },
];

export default function About() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string | null) ?? '';
    if (honeypot.trim()) {
      setStatus('success');
      setMessage('感谢你的安利！我们会尽快审核收录。');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    setStatus('loading');
    setMessage('');
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('安利投稿成功！编辑部会尽快审核并收录到对应清单。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，请稍后再试。');
    }
  }

  return (
    <section id="about" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
          <div>
            <span className="text-sm font-medium text-accent-700">ABOUT 178漫画网</span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">关于178漫画网</h2>
            <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
              178漫画网诞生于一群"剧荒星人"的苦恼——好作品那么多，却总不知道该追哪部。于是我们把漫友的真实安利、编辑部的逐部试读，汇聚成一份值得信赖的漫画安利推荐清单。
            </p>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              我们坚持"内容为王、质量为上"的甄选原则：每一部收录作品都经过人工把关，拒绝注水与虚假好评。无论是热血、治愈还是悬疑，只推荐真正值得花时间的好漫。
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg border border-background-200 bg-background-50 p-4 text-center">
                  <span className="block font-heading text-2xl text-primary-600">{s.value}</span>
                  <span className="mt-1 block text-xs text-foreground-600">{s.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-accent-200 bg-accent-50 p-5">
              <div className="flex items-center gap-2">
                <i className="ri-heart-3-fill text-lg text-accent-600" aria-hidden="true"></i>
                <span className="font-medium text-accent-900">我们的理念</span>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-accent-900">
                "一部好漫，值得被更多人看见。"我们相信，真诚的安利是漫画社区最好的连接。你的每一次投稿与评价，都在帮助更多漫友找到心仪的作品。
              </p>
            </div>
          </div>

          <div id="submit" className="scroll-mt-24 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-xl text-foreground-950">投稿安利一部好漫</h3>
            <p className="mt-2 text-sm text-foreground-600">
              把你压箱底的佳作分享给更多漫友吧。审核通过后，会收录进对应的好漫推荐清单。
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-4"
              noValidate
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="title" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    作品名称 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    required
                    placeholder="请输入漫画作品名"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    作品分类
                  </label>
                  <select
                    id="category"
                    name="category"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="">请选择分类</option>
                    <option value="热血冒险">热血冒险</option>
                    <option value="奇幻异世界">奇幻异世界</option>
                    <option value="治愈日常">治愈日常</option>
                    <option value="悬疑推理">悬疑推理</option>
                    <option value="科幻末世">科幻末世</option>
                    <option value="青春恋爱">青春恋爱</option>
                    <option value="运动竞技">运动竞技</option>
                    <option value="校园成长">校园成长</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="author" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  作者
                </label>
                <input
                  id="author"
                  name="author"
                  type="text"
                  placeholder="请输入作者名"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                />
              </div>

              <div>
                <label htmlFor="reason" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  推荐理由 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="为什么值得看？剧情、画风、人设……说点什么打动大家吧"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                ></textarea>
                <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="nickname" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的昵称
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    type="text"
                    placeholder="怎么称呼你"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系邮箱
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="选填，用于收录通知"
                    className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  />
                </div>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot-field"
              />

              <button
                type="submit"
                disabled={status === 'loading'}
                className="mt-2 inline-flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill" aria-hidden="true"></i>
                    提交安利
                  </>
                )}
              </button>

              {status === 'success' && (
                <p className="rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-fill mr-1" aria-hidden="true"></i>
                  {message}
                </p>
              )}
              {status === 'error' && message && (
                <p className="rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
                  <i className="ri-error-warning-fill mr-1" aria-hidden="true"></i>
                  {message}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}